import {
  Box,
  Button,
  Icon,
  Skeleton,
  SxProps,
  Typography
} from "@outschool/backpack";
import { ListingCarousel_ListingFragment } from "@outschool/gql-frontend-generated";
import { faChevronDown, faTimes } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { NudgeModal } from "@outschool/ui-components-shared";
import { Loading as Spinner } from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import React from "react";

import { CardListing } from "../../../shared/parentDashboard/CardDefinition";
import useParentDashboardFeaturedCategories, {
  FeaturedCategory
} from "../../hooks/useParentDashboardFeaturedCategories";
import useParentDashboardFeaturedQueries from "../../hooks/useParentDashboardFeaturedQueries";
import { useActiveLearner } from "../../providers/ActiveLearnerProvider";
import ListingCarousel from "../listings/carousels/ListingCarousel";
import CardClassList from "../parentDashboard/cards/CardClassList";

const MOBILE_LISTING_COUNT = 9;
const DESKTOP_LISTING_COUNT = 10;

const FeaturedClasses = () => {
  const { t } = useTranslation(
    "client\\components\\loggedInHome\\FeaturedClasses"
  );
  const isMobile = useIsMobile();
  const categories = useParentDashboardFeaturedCategories();
  const preloadCategories = categories.filter(c => c.preload);
  const { activeLearner } = useActiveLearner({
    onActiveLearnerChanged() {
      setInitialized(false);
      setSelectedCategory(categories[0]);
    }
  });

  const [isCategoryModalOpen, setIsCategoryModalOpen] = React.useState(false);
  const [selectedCategory, setSelectedCategory] =
    React.useState<FeaturedCategory>(categories[0]);

  const { loading, data, fallbackData, preloadFinished } =
    useParentDashboardFeaturedQueries({
      selectedCategory,
      preloadCategories,
      limit: DESKTOP_LISTING_COUNT
    });

  const [initialized, setInitialized] = React.useState(false);
  const showSpinner = !preloadFinished;

  React.useEffect(() => {
    if (!showSpinner && !initialized) {
      setInitialized(true);

      if (preloadCategories.length === 0) {
        handleCategorySelect(categories[0]);
        return;
      }

      for (
        let targetIndex = 0;
        targetIndex < preloadCategories.length;
        targetIndex++
      ) {
        const categoryId = preloadCategories[targetIndex].id;
        if (data[categoryId]?.length > 0) {
          handleCategorySelect(categories[targetIndex]);
          return;
        }
      }

      const nonPreloadCategory = categories.filter(c => !c.preload)[0];
      if (nonPreloadCategory) {
        handleCategorySelect(nonPreloadCategory);
        return;
      }

      handleCategorySelect(categories[0]);
    }
  }, [showSpinner, data, categories, preloadCategories, initialized]);

  const { showLoading, listings } = React.useMemo(() => {
    if (!selectedCategory || loading || !initialized) {
      return { showLoading: true, listings: [] };
    }

    const currentData = data[selectedCategory.id];
    if (!currentData) {
      return { showLoading: true, listings: [] };
    }

    const categoryData = currentData.length > 0 ? currentData : fallbackData;
    const maxCount = isMobile ? MOBILE_LISTING_COUNT : DESKTOP_LISTING_COUNT;
    return {
      showLoading: false,
      listings: categoryData.slice(0, maxCount)
    };
  }, [data, selectedCategory, fallbackData, isMobile, loading, initialized]);

  const handleCategorySelect = (category: FeaturedCategory) => {
    setSelectedCategory(category);
    setIsCategoryModalOpen(false);
  };

  const filteredCategories = categories.filter(
    c => !c.preload || data[c.id]?.length > 0
  );

  const title = activeLearner
    ? t("Recommended classes for {{learnerName}}", {
        learnerName: activeLearner.name
      })
    : t("Recommended classes");

  if (isMobile) {
    return (
      <Box
        flex
        sx={{ flexDirection: "column", gap: 20, marginBottom: "4.8rem" }}
      >
        <Typography variant="h4" sx={{ textAlign: "center" }}>
          {title}
        </Typography>
        {showSpinner ? (
          <Spinner />
        ) : (
          <Box
            flex
            sx={{
              flexDirection: "column",
              color: "grey.900",
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.25)",
              borderRadius: "1em"
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                borderBottom: "1px solid",
                borderColor: "grey.100",
                overflowX: "hidden"
              }}
            >
              <Button
                variant="text"
                onClick={() => setIsCategoryModalOpen(true)}
                sx={{
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "1.6rem 1.8rem",
                  backgroundColor: "transparent !important"
                }}
              >
                <Typography variant="button" sx={{ fontSize: "1.6rem" }}>
                  {selectedCategory.title}
                </Typography>
                <Icon icon={faChevronDown} />
              </Button>
            </Box>
            <Box
              sx={{
                padding: "1.6rem",
                overflow: "hidden"
              }}
            >
              {showLoading ? (
                <Skeletons isMobile />
              ) : (
                <CardClassList
                  id={selectedCategory.id}
                  listings={listings as CardListing[]}
                  trackingProperties={{}}
                />
              )}
            </Box>
          </Box>
        )}
        <NudgeModal
          open={isCategoryModalOpen}
          onClose={() => setIsCategoryModalOpen(false)}
        >
          <Box sx={{ position: "relative", padding: "0.4rem 0" }}>
            <Icon
              icon={faTimes}
              onClick={() => setIsCategoryModalOpen(false)}
              sx={{
                position: "absolute",
                top: "-0.4rem",
                right: "-0.6rem",
                width: "3.2rem",
                height: "3.2rem",
                padding: "0.4rem",
                zIndex: 1,
                cursor: "pointer"
              }}
            />
            <Categories
              selectedCategory={selectedCategory}
              categories={filteredCategories}
              setSelectedCategory={handleCategorySelect}
              sx={{ maxHeight: "80vh" }}
            />
          </Box>
        </NudgeModal>
      </Box>
    );
  }

  return (
    <Box flex sx={{ flexDirection: "column", gap: 24, marginBottom: "4.8rem" }}>
      <Typography variant="h5" sx={{ fontWeight: 700, textAlign: "center" }}>
        {title}
      </Typography>
      {showSpinner ? (
        <Spinner />
      ) : (
        <Box
          flex
          sx={{
            flexDirection: "row",
            padding: "0.4rem 0",
            borderColor: "grey.100",
            borderStyle: "solid",
            borderWidth: "1px",
            borderRadius: "8px"
          }}
        >
          <Categories
            selectedCategory={selectedCategory}
            categories={filteredCategories}
            setSelectedCategory={handleCategorySelect}
            sx={{ maxHeight: 420, marginTop: "2.8rem", marginLeft: "3rem" }}
          />
          <Box
            flex
            sx={{ flex: 1, flexDirection: "column", overflow: "hidden" }}
          >
            {showLoading ? (
              <Skeletons />
            ) : (
              <ListingCarousel
                listings={listings as ListingCarousel_ListingFragment[]}
                trackingLabel={selectedCategory.trackingLabel}
                carouselRowNum={categories.findIndex(
                  c => c.id === selectedCategory.id
                )}
                sx={{ padding: 0 }}
                outerContainerSx={{ padding: 0 }}
                leftAlign
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const Categories = ({
  selectedCategory,
  categories,
  setSelectedCategory,
  sx
}: {
  selectedCategory: FeaturedCategory;
  categories: FeaturedCategory[];
  setSelectedCategory: (category: FeaturedCategory) => void;
  sx?: SxProps;
}) => {
  const { t } = useTranslation(
    "client\\components\\loggedInHome\\FeaturedClasses"
  );
  return (
    <Box flex sx={{ position: "relative", flexDirection: "column", ...sx }}>
      <Typography
        variant="caption"
        sx={{
          textTransform: "uppercase",
          color: "grey.600",
          fontWeight: "bold",
          marginBottom: "0.4rem"
        }}
      >
        {t("Browse by:")}
      </Typography>
      <Box
        flex
        sx={{
          position: "relative",
          flex: 1,
          flexDirection: "column",
          overflow: "auto",
          padding: "1rem 1.6rem 2.4rem 0",
          gap: "0.8rem"
        }}
      >
        {categories.map(item => {
          const selected = item.id === selectedCategory.id;
          return (
            <Button
              key={item.id}
              variant="text"
              onClick={() => setSelectedCategory(item)}
              sx={{
                position: "relative",
                padding: "0.6rem 0",
                justifyContent: "start",
                backgroundColor: "transparent !important"
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  pointerEvents: "none",
                  visibility: "hidden"
                }}
              >
                {item.title}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  position: "absolute",
                  fontWeight: selected ? 700 : 500,
                  color: selected ? "primary.main" : "black"
                }}
              >
                {item.title}
              </Typography>
            </Button>
          );
        })}
      </Box>
      <Box
        sx={{
          position: "absolute",
          top: "2.2rem",
          left: 0,
          right: 0,
          height: "1.6rem",
          background: "linear-gradient(to bottom, white, transparent)",
          zIndex: 1,
          pointerEvents: "none"
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "2.8rem",
          background: "linear-gradient(to top, white, transparent)",
          zIndex: 1,
          pointerEvents: "none"
        }}
      />
    </Box>
  );
};

const Skeletons = ({ isMobile }: { isMobile?: boolean }) => {
  if (isMobile) {
    return (
      <Box
        sx={{
          position: "relative",
          display: "grid",
          gridTemplateRows: "repeat(3, 1fr)",
          gridAutoColumns: "70vw",
          gridAutoFlow: "column",
          gap: 10,
          wordBreak: "break-word",
          overflow: "hidden"
        }}
      >
        {Array.from({ length: MOBILE_LISTING_COUNT }).map((_, index) => (
          <MobileSkeleton key={index} />
        ))}
        <Box
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: 16,
            background: "linear-gradient(to right, transparent, white)",
            pointerEvents: "none",
            zIndex: 1
          }}
        />
      </Box>
    );
  }
  return (
    <Box
      flex
      sx={{
        position: "relative",
        flex: 1,
        flexDirection: "row",
        flexWrap: "nowrap",
        gap: 20,
        padding: "1.6rem",
        overflow: "hidden",
        minWidth: 0
      }}
    >
      {Array.from({ length: DESKTOP_LISTING_COUNT }).map((_, index) => (
        <CarourselSkeleton key={index} />
      ))}
      <Box
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: 68,
          background: "linear-gradient(to right, transparent 0%, white 30%)",
          pointerEvents: "none",
          zIndex: 1
        }}
      />
    </Box>
  );
};

const MobileSkeleton = () => {
  return (
    <Box flex sx={{ flexDirection: "row", gap: 12 }}>
      <Skeleton
        variant="rectangular"
        height={72}
        width={72}
        sx={{ borderRadius: 12 }}
      />
      <Box sx={{ flex: 1 }}>
        <Skeleton
          variant="rectangular"
          height={22}
          width={156}
          sx={{ borderRadius: 8, marginTop: "0.6rem" }}
        />
        <Skeleton
          variant="rectangular"
          height={22}
          width={100}
          sx={{ borderRadius: 8, marginTop: "0.6rem" }}
        />
      </Box>
    </Box>
  );
};

const CarourselSkeleton = () => {
  const borderRadius = 8;
  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        flexShrink: 0,
        width: 242,
        height: 412,
        border: "1px solid #DDDFE3",
        borderRadius,
        overflow: "hidden"
      }}
    >
      <Skeleton variant="rectangular" height={140} width={242} />
      <Box flex sx={{ flex: 1, flexDirection: "column", gap: 6, padding: 16 }}>
        <Skeleton
          variant="rectangular"
          height={22}
          width={180}
          sx={{ borderRadius }}
        />
        <Skeleton
          variant="rectangular"
          height={22}
          width={160}
          sx={{ borderRadius }}
        />
      </Box>
      <Box flex sx={{ flexDirection: "row", gap: 8, padding: 12 }}>
        <Skeleton
          variant="rectangular"
          height={58}
          sx={{ flex: 1, borderRadius }}
        />
        <Skeleton
          variant="rectangular"
          height={58}
          sx={{ flex: 1, borderRadius }}
        />
        <Skeleton
          variant="rectangular"
          height={58}
          sx={{ flex: 1, borderRadius }}
        />
      </Box>
    </Box>
  );
};

export default FeaturedClasses;
